import {
  type Dispatch,
  type SetStateAction,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

export type UseTagLikeEditDirtyStateParams<T> = {
  /** URL segment for the edited entity (e.g. tag id); when it changes, baselines reset. */
  routeKey: string;
  /** Name from the server for the current route; used to seed baseline and field. */
  loadedTagName: string | undefined;
  tagName: string;
  setTagName: Dispatch<SetStateAction<string>>;
  selectedRows: T[];
  persistedRows: T[] | undefined;
  /** Stable key per row (e.g. `location_code`) for membership baseline / dirty checks. */
  getMemberKey: (row: T) => string;
  inputBoxStatus: string;
  setInputBoxStatus: Dispatch<SetStateAction<string>>;
  /** When `inputBoxStatus` equals this, `hasNameChanged` is false (duplicate / unique constraint). */
  duplicateNameAlertMessage: string;
};

/**
 * Baselines + dirty flags for the "rename + checkbox membership" edit flow on the location
 * tag edit page. Kept local to this feature; can be promoted to a shared hook if other tag
 * edit pages adopt the same dirty-state contract.
 */
export function useTagLikeEditDirtyState<T>({
  routeKey,
  loadedTagName,
  tagName,
  setTagName,
  selectedRows,
  persistedRows,
  getMemberKey,
  inputBoxStatus,
  setInputBoxStatus,
  duplicateNameAlertMessage,
}: UseTagLikeEditDirtyStateParams<T>) {
  const initialTagNameRef = useRef<string>('');
  const lastRouteKeyRef = useRef<string>('');

  useEffect(() => {
    if (routeKey !== lastRouteKeyRef.current) {
      lastRouteKeyRef.current = routeKey;
      initialTagNameRef.current = '';
    }
  }, [routeKey]);

  useEffect(() => {
    if (!loadedTagName) return;

    if (routeKey !== lastRouteKeyRef.current || !initialTagNameRef.current) {
      initialTagNameRef.current = loadedTagName;
      setTagName(loadedTagName);
    }
  }, [loadedTagName, routeKey, setTagName]);

  const handleTagNameChange = useCallback(
    (value: string) => {
      startTransition(() => {
        setTagName(value);

        const trimValue = value.trim();
        if (trimValue === initialTagNameRef.current || trimValue === '') {
          setInputBoxStatus('');
        } else {
          setInputBoxStatus('changed');
        }
      });
    },
    [setInputBoxStatus, setTagName],
  );

  // Derive baseline membership directly from `persistedRows` so the comparison
  // is correct on the first render. Reading from `initialMemberKeysRef` here
  // would go stale because the ref is populated in a `useEffect` that runs
  // after render — once `selectedRows` is non-empty on first render (e.g.
  // because the upstream view list is now memoised synchronously), the
  // useMemo would never re-run to pick up the populated baseline.
  const hasCheckboxesChanged = useMemo(() => {
    const currentKeys = new Set(selectedRows.map((row) => getMemberKey(row)));
    const initialKeys = new Set(
      (persistedRows ?? []).map((row) => getMemberKey(row)),
    );

    return (
      currentKeys.size !== initialKeys.size ||
      !Array.from(currentKeys).every((k) => initialKeys.has(k))
    );
  }, [getMemberKey, persistedRows, selectedRows]);

  const hasNameChanged = useMemo(() => {
    const trimmedName = tagName.trim();
    const duplicateBlocked = inputBoxStatus === duplicateNameAlertMessage;

    return (
      trimmedName !== '' &&
      trimmedName !== initialTagNameRef.current &&
      !duplicateBlocked
    );
  }, [duplicateNameAlertMessage, inputBoxStatus, tagName]);

  const isSaveDisabled = useMemo(
    () => !(hasNameChanged || hasCheckboxesChanged),
    [hasCheckboxesChanged, hasNameChanged],
  );

  return {
    handleTagNameChange,
    hasCheckboxesChanged,
    hasNameChanged,
    isSaveDisabled,
  };
}
